import { differenceInCalendarDays, isValid as isValidDate, parseISO } from "date-fns";
import flatten from "lodash/flatten";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import mergeWith from "lodash/mergeWith";
import reject from "lodash/reject";
import orderBy from "lodash/orderBy";
import pick from "lodash/pick";
import unionBy from "lodash/unionBy";
import uniqueId from "lodash/uniqueId";
import values from "lodash/values";

import { earliestDayIn, latestDayIn } from "../../utils/entry";

const entriesVersion = () => uniqueId("entries-version-");

const _initialState = {
  bounds: { earliest: null, latest: null },
  dayGroups: [],
  daysSinceEntry: 0,
  entries: [],
  entriesInTransit: false,
  hasMore: false,
  hours: 0,
  lastChanged: new Date().getTime(),
  mostRecent: null,
  page: 0,
  pageSize: 100,
  sortDir: "desc",
  submitGroup: {},
  submittedHours: 0,
  // When we switch or reset state, we need to change this.
  version: entriesVersion(),
};

// These are actions we need to guard based on the entries version.
const VERSIONED_ACTIONS = ["ADVANCE_CURRENT_PAGE", "FETCHED_ENTRIES", "SET_HAS_MORE_ENTRIES", "SET_ENTRIES", "SET_DAY_GROUPS", "SET_WORKED_HOURS"];

export default function entries(state = _initialState, action) {
  if (versionChanged(state, action)) {
    return state;
  }

  switch (action.type) {
  case "FETCHING_ENTRIES":
    return { ...state, entriesInTransit: true };
  case "FETCHED_ENTRIES":
    return { ...state, entriesInTransit: false };
  case "ADVANCE_CURRENT_PAGE":
    return { ...state, page: state.page + 1 };
  case "SET_HAS_MORE_ENTRIES":
    return { ...state, hasMore: action.hasMore };
  case "DELETE_ENTRY":
  case "INCREMENT_ENTRY_OPS":
  case "SUBMIT_ENTRY":
  case "SAVED_ENTRY":
    return onEntryAction(state, action);
  case "CLEAR_ENTRIES":
    return clearEntries(state, action);
  case "SET_MOST_RECENT_ENTRY":
    return setMostRecentEntry(state, action);
  case "SET_ENTRIES":
    return mergeInEntries(state, action);
  case "SET_DAY_GROUPS":
    return setDayGroups(state, action);
  case "SET_SORT_DIR":
    return setSortDir(state, action);
  case "SET_WORKED_HOURS":
    return setWorkedHours(state, action);
  case "SUBMIT_GROUP_RESET":
    return resetSubmitGroup(state, action);
  case "SUBMIT_GROUP_TOGGLE_ENTRIES":
    return toggleSubmitGroupEntries(state, action);
  case "SUBMIT_GROUP_TOGGLE_ENTRY":
    return toggleSubmitGroupEntry(state, action);
  default:
    return state;
  }
}

/**
 * @param {object} state
 * @return {object}
 */
function clearEntries(state, action) {
  const cleanState = pick(_initialState, ["dayGroups", "entries", "hasMore", "page", "submitGroup"]);

  if (shouldFilterExistingEntries(action)) {
    const { startDate, endDate } = action.filters.current;

    const filterEntryOrDay = function({ stringDate }) {
      if (startDate && stringDate < startDate) {
        return false;
      }

      if (endDate && stringDate > endDate) {
        return false;
      }

      return true;
    }

    cleanState.entries = state.entries.filter(filterEntryOrDay);
    cleanState.dayGroups = state.dayGroups.filter(filterEntryOrDay);
  }

  const version = entriesVersion();

  return { ...state, ...cleanState, version };
}

function shouldFilterExistingEntries(action) {
  if (action.because !== "historyContext") {
    return false;
  }

  if (!(action.filters && action.filters.current)) {
    return false;
  }

  return true;
}

/**
 * @param {object} state
 * @param {object[]} state.entries
 * @param {object} action
 * @param {object} action.entry
 * @param {string} action.entry.id
 * @return {object}
 */
function deleteEntry(state, action) {
  const deletedId = get(action, "entry.id");

  if (!deletedId) {
    return state;
  }

  const entries = reject(state.entries, { id: deletedId });

  return { ...state, entries };
}

/**
 * @param {object} state
 * @return {object}
 */
function onEntryAction(state, action) {
  let newState = markLastChanged(state);

  if (get(action, "type") === "DELETE_ACTION") {
    newState = deleteEntry(state, action);
  }

  return newState;
}

function markLastChanged(state) {
  const lastChanged = new Date().getTime();

  return { ...state, lastChanged };
}

function mergeInEntries(state, action) {
  const initial = state.page === 0;

  if (initial) {
    const { entries } = action;

    const bounds = calculateBoundsFor(entries);

    return { ...state, bounds, entries };
  }

  const existing = groupBy(state.entries, "key");
  const incoming = groupBy(action.entries, "key");

  mergeWith(existing, incoming, mergeEntriesInSameDay);

  const combined = flatten(values(existing));

  const entries = orderBy(combined, ["key"], [state.sortDir]);

  const bounds = calculateBoundsFor(entries);

  return { ...state, bounds, entries };
}

/**
 * @param {object[]} entries
 * @return {object}
 */
function calculateBoundsFor(entries) {
  const earliest = earliestDayIn(entries);
  const latest = latestDayIn(entries);

  return { earliest, latest };
}

/**
 * @param {object[]} existing
 * @param {object[]} incoming
 * @return {object[]}
 */
function mergeEntriesInSameDay(existing, incoming) {
  const combined = unionBy(incoming, existing, uniqueEntry);

  if (combined.length > 1) {
    // Ensure we don't have any lingering `noEntries` with new data
    return reject(combined, "noEntries");
  }

  return combined;
}

const NO_ENTRIES = Symbol("NO_ENTRIES");

function uniqueEntry(entry) {
  if (entry) {
    if (entry.id) {
      return entry.id
    } else if (entry.noEntries) {
      return NO_ENTRIES;
    }
  }

  return entry;
}

function resetSubmitGroup(state, _) {
  return { ...state, submitGroup: {} };
}

function setDayGroups(state, action) {
  const dayGroups = get(action, "dayGroups", []);

  return {
    ...state,
    dayGroups
  };
}

function setMostRecentEntry(state, { mostRecent }) {
  let daysSinceEntry = 0;

  const entryDate = get(mostRecent, "entryDate", null);

  const date = parseISO(entryDate);

  if (!isValidDate(date)) {
    return { ...state, mostRecent: null, daysSinceEntry: 0 };
  }

  daysSinceEntry = differenceInCalendarDays(new Date(), date);

  if (daysSinceEntry < 0) {
    daysSinceEntry = 0;
  }

  return { ...state, mostRecent, daysSinceEntry };
}

const SORTS = ["asc", "desc"];

function setSortDir(state, action) {
  const { sortDir } = action;

  const dayGroups = orderBy(state.dayGroups, ["key"], [sortDir]);

  const entries = orderBy(state.entries, ["key"], [sortDir]);

  return { ...state, sortDir, dayGroups, entries };
}

function setWorkedHours(state, action) {
  const { hours, submittedHours } = action;

  return { ...state, hours, submittedHours };
}

function toggleSubmitGroupEntries(state, action) {
  const entries = get(action, "entries", []);

  const entryIds = entries.map(({ id }) => id);

  const enabled = Boolean(get(action, "enabled", false));

  const submitGroup = {};

  for (const { id: entryId } of entries) {
    submitGroup[entryId] = enabled;
  }

  return { ...state, submitGroup };
}

function toggleSubmitGroupEntry(state, action) {
  const { submitGroup } = state;

  const entryId = get(action, "entry.id");
  const enabled = Boolean(get(action, "enabled", false));

  if (entryId && get(state, ["submitGroup", entryId]) !== enabled) {
    const submitGroup = Object.assign({}, state.submitGroup);

    submitGroup[entryId] = enabled;

    return { ...state, submitGroup };
  }

  return state;
}

function versionChanged(state, action) {
  if (!VERSIONED_ACTIONS.includes(action.type)) {
    return false
  }

  const current = state.version;
  const incoming = action.version;

  if (action.version == null) {
    console.warn("%s needs to include a version", action.type);

    return false;
  }

  if (current !== incoming) {
    console.debug("entries version changed in %s (for %s), discarding", action.type, incoming);

    return true;
  }

  return false;
}



// WEBPACK FOOTER //
// ./app/reducers/history/entries.js